'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import CaseStudyModal from '@/components/ui/CaseStudyModal';
import { caseStudies, CaseStudy } from '@/data/case-studies';
import {
  Code,
  Activity,
  Network,
  Shield,
  Cpu,
  CarFront,
  HousePlug,
  Barcode,
  Database,
  TrendingUp,
  HelpCircle,
} from 'lucide-react';

export default function CaseStudies() {
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<CaseStudy | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Animation configuration for staggered entrance
  const getCardAnimationProps = (index: number) => ({
    initial: { opacity: 0, y: 50, scale: 0.95 },
    whileInView: { opacity: 1, y: 0, scale: 1 },
    viewport: { once: true, amount: 0.3 },
    transition: {
      duration: 0.6,
      delay: index * 0.1,
      type: 'spring' as const,
      stiffness: 100,
    },
  });

  // Helper function to render Lucide React icons
  const renderIcon = (iconName: string) => {
    // Map icon names to components
    const iconMap: Record<
      string,
      React.ComponentType<{ className?: string }>
    > = {
      Code: Code,
      Barcode: Barcode,
      CarFront: CarFront,
      HousePlug: HousePlug,
      Activity: Activity,
      Network: Network,
      Shield: Shield,
      Cpu: Cpu,
      Database: Database,
      TrendingUp: TrendingUp,
    };

    const IconComponent = iconMap[iconName];

    if (IconComponent) {
      return (
        <IconComponent className="h-6 w-6 text-white drop-shadow-lg sm:h-7 sm:w-7" />
      );
    }
    // Fallback to a default icon if the icon name is not found
    return (
      <HelpCircle className="h-6 w-6 text-white drop-shadow-lg sm:h-7 sm:w-7" />
    );
  };

  const handleCaseStudyClick = (caseStudy: CaseStudy) => {
    setSelectedCaseStudy(caseStudy);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCaseStudy(null);
  };

  return (
    <div className="relative">
      <div className="w-full">
        <div className="min-h-screen">
          <main>
            <div className="pb-20">
              <div className="mx-auto max-w-7xl px-4">
                {/* Case Study Grid */}
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {caseStudies.map((caseStudy, index) => (
                    <motion.div
                      key={caseStudy.id}
                      className="group transform:none relative"
                      onClick={() => handleCaseStudyClick(caseStudy)}
                      {...getCardAnimationProps(index)}
                      whileHover={{
                        y: -8,
                        transition: { duration: 0.3, ease: 'easeOut' },
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div
                        className={`from-card/80 to-card/40 border-border/50 hover:border-primary/30 relative h-[550px] transform-gpu overflow-hidden rounded-2xl border bg-gradient-to-br shadow-xl backdrop-blur-xl transition-all duration-500 group-hover:scale-[1.02] hover:shadow-2xl sm:h-[550px] sm:rounded-3xl lg:h-[550px]`}
                      >
                        <div
                          className={`absolute inset-0 bg-gradient-to-br ${caseStudy.gradient.from} ${caseStudy.gradient.to} opacity-0 transition-opacity duration-500 group-hover:opacity-20`}
                        ></div>
                        <div className="bg-grid-white/5 absolute inset-0 bg-[length:20px_20px] opacity-[0.03]"></div>
                        <div className="relative z-10 flex h-full flex-col justify-between p-5 sm:p-6 lg:p-7">
                          <div className="mb-4 flex flex-col gap-3 sm:mb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-0">
                            <div className="flex-1">
                              <div className="mb-3 flex items-center gap-3 sm:gap-4">
                                <motion.div
                                  className={`border-border/30 flex h-12 w-12 items-center justify-center rounded-xl border bg-gradient-to-br ${caseStudy.gradient.from} ${caseStudy.gradient.to} shadow-lg sm:h-14 sm:w-14 sm:rounded-2xl`}
                                  whileHover={{
                                    rotate: 5,
                                    scale: 1.05,
                                    transition: { duration: 0.2 },
                                  }}
                                >
                                  {renderIcon(caseStudy.icon)}
                                </motion.div>
                                <div>
                                  <span className="bg-primary/10 text-primary border-primary/20 inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-bold sm:px-4">
                                    {caseStudy.category}
                                  </span>
                                </div>
                              </div>
                              <div className="text-foreground group-hover:text-primary mb-3 text-xl leading-tight font-bold transition-colors duration-300 sm:text-2xl lg:text-xl">
                                {caseStudy.title}
                              </div>
                              <p className="text-muted-foreground line-clamp-2 max-h-12 overflow-hidden text-sm leading-relaxed sm:text-base">
                                {caseStudy.description}
                              </p>
                            </div>
                            {caseStudies.indexOf(caseStudy) < 2 && (
                              <motion.div
                                className="mt-0.5 ml-3 flex w-fit items-center gap-1.5 rounded-full border border-amber-500/30 bg-gradient-to-r from-amber-500/20 to-orange-500/20 px-3.5 py-1.5 text-amber-600 shadow-sm sm:mt-0 dark:text-amber-400"
                                style={{ transform: 'scale(1.04081)' }}
                                animate={{
                                  scale: [1, 1.05, 1],
                                  rotate: [0, 5, 0],
                                }}
                                transition={{
                                  duration: 2,
                                  repeat: Infinity,
                                  ease: 'easeInOut',
                                }}
                              >
                                <svg
                                  stroke="currentColor"
                                  fill="none"
                                  strokeWidth="2"
                                  viewBox="0 0 24 24"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="h-3.5 w-3.5"
                                  height="1em"
                                  width="1em"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                                </svg>
                                <span className="text-xs font-bold">
                                  Featured
                                </span>
                              </motion.div>
                            )}
                          </div>
                          <div className="mb-2 flex h-8 flex-wrap items-center gap-2 sm:mb-3">
                            <span className="bg-card/70 hover:bg-card/90 text-muted-foreground border-border/20 inline-flex items-center gap-2 rounded-lg border px-2.5 py-1 text-xs transition-colors">
                              <svg
                                stroke="currentColor"
                                fill="none"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-primary/80 h-3 w-3"
                                height="1em"
                                width="1em"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  x="2"
                                  y="7"
                                  width="20"
                                  height="14"
                                  rx="2"
                                  ry="2"
                                ></rect>
                                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                              </svg>
                              <span>{caseStudy.client}</span>
                            </span>
                            <span className="bg-card/70 hover:bg-card/90 text-muted-foreground border-border/20 inline-flex items-center gap-2 rounded-lg border px-2.5 py-1 text-xs transition-colors">
                              <svg
                                stroke="currentColor"
                                fill="none"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-primary/80 h-3 w-3"
                                height="1em"
                                width="1em"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  x="2"
                                  y="7"
                                  width="20"
                                  height="14"
                                  rx="2"
                                  ry="2"
                                ></rect>
                                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                              </svg>
                              <span>{caseStudy.duration}</span>
                            </span>
                          </div>

                          <div className="mb-3 grid h-20 grid-cols-3 gap-2 sm:mb-4 sm:gap-3">
                            {caseStudy.info.map((info, index) => (
                              <motion.div
                                key={index}
                                className="bg-muted/40 border-border/40 hover:bg-muted/60 hover:border-border/60 rounded-lg border p-2 text-center transition-all duration-300 sm:rounded-xl sm:p-3"
                                whileHover={{
                                  scale: 1.05,
                                  backgroundColor: 'rgba(var(--muted), 0.6)',
                                  transition: { duration: 0.2 },
                                }}
                              >
                                <span className="text-foreground text-base font-bold sm:text-lg">
                                  {info.number}
                                </span>
                                <div className="text-muted-foreground text-xs leading-tight capitalize">
                                  {info.name}
                                </div>
                              </motion.div>
                            ))}
                          </div>

                          <div className="mb-3 h-16 sm:mb-4">
                            <div className="flex flex-wrap gap-1.5 sm:gap-2">
                              {caseStudy.technologies
                                .slice(0, 4)
                                .map((tech, index) => (
                                  <motion.span
                                    key={index}
                                    className="bg-secondary/10 text-secondary-foreground border-secondary/30 hover:border-secondary/50 rounded-md border px-2 py-1 text-xs font-medium transition-all duration-300 sm:rounded-lg sm:px-3"
                                    whileHover={{
                                      scale: 1.1,
                                      y: -2,
                                      transition: { duration: 0.2 },
                                    }}
                                  >
                                    {tech}
                                  </motion.span>
                                ))}
                              {caseStudy.technologies.length > 4 && (
                                <span className="bg-muted/50 text-muted-foreground rounded-md px-2 py-1 text-xs font-medium sm:rounded-lg sm:px-3">
                                  +{caseStudy.technologies.length - 4} more
                                </span>
                              )}
                            </div>
                          </div>
                          <div>
                            <motion.button
                              className="from-primary/60 focus:ring-primary/50 group relative h-[50px] w-full overflow-hidden rounded-xl border border-white/20 bg-gradient-to-r to-blue-500/20 text-sm font-bold text-white shadow-lg transition-all duration-300 hover:shadow-xl focus:ring-2 focus:ring-offset-2 focus:outline-none sm:text-base cursor-pointer"
                              aria-label="Read full case study"
                              whileHover={{
                                scale: 1.02,
                                transition: { duration: 0.2 },
                              }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <div className="absolute inset-0 h-full w-full bg-gradient-to-r from-white/5 to-white/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
                              <div className="flex items-center justify-center gap-2 transition-all duration-300 group-hover:gap-3">
                                <span>FULL CASE STUDY</span>
                                <svg
                                  stroke="currentColor"
                                  fill="none"
                                  strokeWidth="2"
                                  viewBox="0 0 24 24"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                                  height="1em"
                                  width="1em"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <line x1="5" y1="12" x2="19" y2="12"></line>
                                  <polyline points="12 5 19 12 12 19"></polyline>
                                </svg>
                              </div>
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Reusable Modal */}
      {selectedCaseStudy && (
        <CaseStudyModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          caseStudy={selectedCaseStudy}
        />
      )}
    </div>
  );
}
